import React, { ReactNode, useContext } from 'react';
import { Redirect, RouteComponentProps, StaticContext } from 'react-router';
import { Route, RouteProps } from 'react-router-dom';
import { OrgFeatures } from '@bb/domain/dist/types';
import { AuthContext } from 'contexts';
import PageRoute from './PageRoute';

type FeatureProtectedRouteProps = RouteProps & {
  requiredFeatures: OrgFeatures[];
  requiredConditions?: boolean[];
  path: string | string[];
  title?: string;
  exact?: boolean;
  component?: React.FC;
  render?: (props: RouteComponentProps<any, StaticContext, {}>) => ReactNode;
};

const FeatureProtectedRoute: React.FC<FeatureProtectedRouteProps> = ({
  requiredFeatures,
  requiredConditions,
  component,
  render,
  title,
  exact,
  path,
}) => {
  const { auth, hasFeature } = useContext(AuthContext);
  const isAssuming = Boolean(auth?.assumingUserId || auth?.isAssumedNotBB);
  const meetsFeatures = requiredFeatures.every(hasFeature);

  const meetsConditions =
    requiredConditions && requiredConditions.length > 0
      ? requiredConditions.every(value => value === true)
      : true;

  // special case for admins who will be redirected to missing-features from '/'
  const paths = typeof path === 'string' ? [path] : path;
  if (
    !(isAssuming || meetsFeatures) &&
    auth.roles[auth.role] >= auth.roles.TEAM_ADMIN &&
    paths.includes('/listing/list/brokerage/:pageNumber')
  ) {
    return (
      <Redirect
        to={
          auth.licenseType === 'licensed'
            ? '/my_business'
            : '/listing/list/favorites/0'
        }
      />
    );
  }

  return isAssuming || (meetsFeatures && meetsConditions) ? (
    title ? (
      <PageRoute
        title={title}
        path={path}
        exact={exact}
        component={component}
        render={render}
      />
    ) : (
      <Route path={path} exact={exact} component={component} render={render} />
    )
  ) : (
    <Redirect to="/feature-missing" />
  );
};

export default FeatureProtectedRoute;
